/**
 * Error handling utilities for encoder tools
 * Supports FR-016: Comprehensive error handling with graceful degradation
 */

import { EncoderToolType } from '../types';

export class EncoderError extends Error {
  constructor(
    message: string,
    public code: string,
    public toolType: EncoderToolType,
    public suggestion?: string
  ) {
    super(message);
    this.name = 'EncoderError';
  }
}

export class ValidationError extends EncoderError {
  constructor(message: string, toolType: EncoderToolType, suggestion?: string) {
    super(message, 'VALIDATION_ERROR', toolType, suggestion);
    this.name = 'ValidationError';
  }
}

export class ProcessingError extends EncoderError {
  constructor(message: string, toolType: EncoderToolType, suggestion?: string) {
    super(message, 'PROCESSING_ERROR', toolType, suggestion);
    this.name = 'ProcessingError';
  }
}

export class FileError extends EncoderError {
  constructor(message: string, toolType: EncoderToolType, suggestion?: string) {
    super(message, 'FILE_ERROR', toolType, suggestion);
    this.name = 'FileError';
  }
}

export class NetworkError extends EncoderError {
  constructor(message: string, toolType: EncoderToolType, suggestion?: string) {
    super(message, 'NETWORK_ERROR', toolType, suggestion);
    this.name = 'NetworkError';
  }
}

export class SecurityError extends EncoderError {
  constructor(message: string, toolType: EncoderToolType, suggestion?: string) {
    super(message, 'SECURITY_ERROR', toolType, suggestion);
    this.name = 'SecurityError';
  }
}

export class ErrorHandler {
  private static readonly ERROR_SUGGESTIONS: Record<string, string> = {
    'INVALID_INPUT': 'Please check your input and try again',
    'EMPTY_INPUT': 'Please enter some text to process',
    'INPUT_TOO_LONG': 'Try using a shorter input or split into multiple parts',
    'INVALID_FILE_TYPE': 'Please use a supported file format',
    'FILE_TOO_LARGE': 'Try using a smaller file or compress it first',
    'PROCESSING_FAILED': 'Please try again or contact support',
    'NETWORK_ERROR': 'Check your internet connection and try again',
    'PERMISSION_DENIED': 'Please grant the necessary permissions',
    'SECURITY_VIOLATION': 'Please ensure your data meets security requirements',
    
    'BASE64_INVALID': 'Ensure the input contains valid Base64 characters only',
    'QR_GENERATE_FAILED': 'Try with shorter text or different error correction level',
    'QR_SCAN_FAILED': 'Ensure the QR code is clear and well-lit',
    'CAMERA_ACCESS_DENIED': 'Grant camera permission in your browser settings',
    'URL_INVALID': 'Enter a complete URL including protocol (http:// or https://)',
    'JWT_INVALID': 'Ensure the token follows the format: header.payload.signature',
    'JWT_EXPIRED': 'Use a valid, non-expired JWT token',
    'CERTIFICATE_INVALID': 'Ensure the file contains a valid X.509 certificate',
    'GZIP_FAILED': 'Try with different compression level or check input format',
    'HTML_ENTITY_INVALID': 'Ensure HTML entities are properly formatted',
  };

  /**
   * Handle errors and return user-friendly message
   */
  static handle(error: unknown, toolType: EncoderToolType): string {
    if (error instanceof EncoderError) {
      return this.formatEncoderError(error);
    }

    if (error instanceof Error) {
      return this.formatGenericError(error, toolType);
    }

    return this.formatUnknownError(toolType);
  }

  /**
   * Create appropriate error based on type
   */
  static createError(
    message: string,
    code: string,
    toolType: EncoderToolType,
    suggestion?: string
  ): EncoderError {
    return new EncoderError(message, code, toolType, suggestion);
  }

  /**
   * Create validation error
   */
  static createValidationError(
    message: string,
    toolType: EncoderToolType,
    suggestion?: string
  ): ValidationError {
    return new ValidationError(message, toolType, suggestion);
  }

  /**
   * Create processing error
   */
  static createProcessingError(
    message: string,
    toolType: EncoderToolType,
    suggestion?: string
  ): ProcessingError {
    return new ProcessingError(message, toolType, suggestion);
  }

  /**
   * Create file error
   */
  static createFileError(
    message: string,
    toolType: EncoderToolType,
    suggestion?: string
  ): FileError {
    return new FileError(message, toolType, suggestion);
  }

  /**
   * Create security error
   */
  static createSecurityError(
    message: string,
    toolType: EncoderToolType,
    suggestion?: string
  ): SecurityError {
    return new SecurityError(message, toolType, suggestion);
  }

  /**
   * Format EncoderError for user display
   */
  private static formatEncoderError(error: EncoderError): string {
    const baseMessage = error.message;
    const suggestion = error.suggestion || this.ERROR_SUGGESTIONS[error.code];
    
    if (suggestion) {
      return `${baseMessage}. ${suggestion}`;
    }
    
    return baseMessage;
  }

  /**
   * Format generic Error for user display
   */
  private static formatGenericError(error: Error, _toolType: EncoderToolType): string {
    const message = error.message || 'An unexpected error occurred';
    return `${message}. Please try again.`;
  }

  /**
   * Format unknown error for user display
   */
  private static formatUnknownError(_toolType: EncoderToolType): string {
    return 'An unexpected error occurred. Please try again or contact support.';
  }

  /**
   * Log error for debugging
   */
  static log(error: unknown, context?: Record<string, any>): void {
    if (error instanceof Error) {
      console.error('Encoder Error:', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        context
      });
    } else {
      console.error('Unknown Error:', { error, context });
    }
  }

  /**
   * Check if error is recoverable
   */
  static isRecoverable(error: EncoderError): boolean {
    const recoverableCodes = [
      'INVALID_INPUT',
      'EMPTY_INPUT',
      'NETWORK_ERROR',
      'PROCESSING_FAILED'
    ];
    
    return recoverableCodes.includes(error.code);
  }

  /**
   * Check if error should be reported to analytics
   */
  static shouldReport(error: EncoderError): boolean {
    const reportableCodes = [
      'PROCESSING_FAILED',
      'SECURITY_ERROR',
      'NETWORK_ERROR'
    ];
    
    return reportableCodes.includes(error.code);
  }
}